import { useLogsTree } from '../api/hooks';
import { ApiError, downloadUrl } from '../api/client';
import { StateMessage } from '../components/StateMessage';
import { formatBytes, formatDateTime } from '../util/format';

/**
 * Logs page: browses the stored-log tree (device → request → files) and offers
 * per-file downloads. Downloads are plain anchors so the browser streams them
 * with Basic Auth applied automatically.
 */
export function LogsPage() {
  const { data: devices, isLoading, isError, error } = useLogsTree();

  if (isLoading) {
    return <StateMessage title="Ładowanie logów…" />;
  }
  if (isError) {
    return (
      <StateMessage tone="error" title="Nie udało się pobrać logów">
        {(error as ApiError)?.message}
      </StateMessage>
    );
  }
  if (!devices || devices.length === 0) {
    return <StateMessage title="Brak zapisanych logów" />;
  }

  return (
    <section>
      <div className="page-head">
        <h2>Logi</h2>
        <span className="muted">{devices.length} urządzeń</span>
      </div>

      {devices.map((device) => (
        <div key={device.deviceHash} className="card">
          <div className="card__head">
            <h3 className="mono">{device.deviceHash}</h3>
            <span className="muted">{device.requests.length} żądań</span>
          </div>

          {device.requests.map((req) => (
            <div key={req.requestId} className="request-block">
              <div className="request-block__head">
                <div>
                  {req.meta ? (
                    <span>
                      <strong>{req.meta.deviceName}</strong>{' '}
                      <span className="muted">· {req.meta.cityName}</span>
                    </span>
                  ) : (
                    <span className="muted small">Brak metadanych (żądanie sprzed tej wersji).</span>
                  )}
                  <div className="mono small muted" title="requestId">
                    {req.requestId}
                  </div>
                </div>
                <span className="muted small">{req.files.length} plików</span>
              </div>
              {req.meta && (
                <div className="request-block__meta muted small">
                  <span>Utworzono: {formatDateTime(req.meta.createdAt)}</span>
                  <span>Odebrano: {formatDateTime(req.meta.receivedAt)}</span>
                  <span title={req.meta.fcmMessageId ?? '—'}>
                    FCM: {req.meta.fcmMessageId ? req.meta.fcmMessageId.split('/').pop() : '—'}
                  </span>
                </div>
              )}
              {req.files.length === 0 ? (
                <p className="muted small">Brak plików (urządzenie nie miało logów).</p>
              ) : (
                <ul className="file-list">
                  {req.files.map((file) => (
                    <li key={file.name} className="file-list__item">
                      <a
                        className="file-list__name mono"
                        href={downloadUrl(device.deviceHash, req.requestId, file.name)}
                      >
                        {file.name}
                      </a>
                      <span className="muted small">{formatBytes(file.size)}</span>
                      <span className="muted small">{formatDateTime(file.modifiedAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ))}
    </section>
  );
}
